import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { RouterState } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { filter, map, Observable, combineLatest, first } from 'rxjs';
import { RootAppState } from '../../app.reducer';
import { selectCxbTenantOptions } from '../../app.selectors';
import { Tenant } from '../../domain/tenant';
import { AuthService } from '../services/auth.service';

@Injectable({
  providedIn: 'root',
})
export class TenantGuard implements CanActivate {
  constructor(
    private readonly store: Store<RootAppState & RouterState>,
    private readonly router: Router,
    private readonly authService: AuthService
  ) {}

  public canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const userId$ = this.authService.user$.pipe(
      filter((user) => !!user),
      map((user) => user?.localAccountId),
      first()
    );

    const tenantOptions$ = this.store.select(selectCxbTenantOptions).pipe(filter((tenantOptions: Tenant[]) => tenantOptions !== null));

    const storedTenant$ = combineLatest([userId$, tenantOptions$]).pipe(
      map(([userId, tenantOptions]) => {
        const savedTenantId = localStorage.getItem(`SELECTED_TENANT_FOR_USER_${userId}`);
        if (!savedTenantId) {
          // There are no saved tenant data
          return null;
        }

        if (!tenantOptions || !tenantOptions.length) {
          // There are no tenant options, no matter what the user saved previously, it is now invalid
          return null;
        }
        // Find the saved tenant in the available tenant options
        const existingTenantOption = tenantOptions.find((to) => to.id === savedTenantId);

        return existingTenantOption || null;
      })
    );

    if (state.url === '/select-tenant') {
      // About to enter the tenant selector page
      return combineLatest([tenantOptions$, storedTenant$, userId$]).pipe(
        map(([tenantOptions, storedTenant, userId]: [Tenant[], Tenant, string]) => {
          if (storedTenant) {
            // There is already a stored tenant, navigate to the default page
            return this.router.createUrlTree(['']);
          }
          if (tenantOptions.length === 0) {
            // No tenant options, navigate to default page
            return this.router.createUrlTree(['']);
          } else if (tenantOptions.length === 1) {
            // Exactly one tenant option, also navigate to default page because it will automatically selected
            return this.router.createUrlTree(['']);
          } else {
            // There are multiple tenant options, allow to enter the /select-tenant route
            return true;
          }
        })
      );
    } else {
      // About to enter anything other than tenant selector page
      return combineLatest([tenantOptions$, storedTenant$, userId$]).pipe(
        map(([tenantOptions, storedTenant, userId]: [Tenant[], Tenant, string]) => {
          if (tenantOptions.length === 0) {
            // No tenant options, nothing to do, allow route
            return true;
          } else if (tenantOptions.length === 1) {
            // Exactly one tenant option, allow the route, the tenant will be selected automatically
            return true;
          } else if (!storedTenant) {
            // There is no stored tenant yet, but there are multiple tenant options, navigate to tenant selector
            return this.router.createUrlTree(['select-tenant']);
          } else {
            // There are multiple tenant options but there is a stored tenant, allow route
            return true;
          }
        })
      );
    }
  }
}
